import React from 'react';
import { FiInbox, FiSmartphone, FiStar, FiBarChart2 } from 'react-icons/fi';
import Hero from '../../components/sections/Hero';
import AuditForm from '../../components/sections/AuditForm';
import SEO from '../../components/common/SEO';
import SafeIcon from '../../components/common/SafeIcon';
import { servicesData } from '../../data/services';

const CRMService = () => {
  const data = servicesData['crm-system'];
  
  const crmFeatures = [
    {
      title: "Unified Smart Inbox",
      desc: "Stop flipping between apps. Every SMS, Email, Facebook message, and Google lead appears in one single, organized thread on your device.",
      icon: FiInbox
    },
    {
      title: "Mobile Office Interface",
      desc: "Run your firm from anywhere. Send professional estimates, capture secure payments via Text-to-Pay, and manage your team from a single app.",
      icon: FiSmartphone
    },
    {
      title: "Reputation Velocity",
      desc: "We automatically request 5-star Google reviews the moment a client interaction is completed, using language that captures high-value keywords.",
      icon: FiStar
    },
    {
      title: "Revenue Transparency",
      desc: "No vanity metrics. Our reports focus on the numbers that move the needle: Leads, Bookings, and real-world ROI.",
      icon: FiBarChart2
    }
  ];

  return (
    <>
      <SEO title={`${data.title} | Alberta SEO Pro`} description={data.description} />
      <Hero 
        title="One Single Source of Truth" 
        subtitle={data.longDesc} 
      />
      
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">The Brain of Your Operations</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Marketing brings the leads; our CRM ensures you close them and turn them into lifelong advocates.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {crmFeatures.map((item, i) => (
              <div key={i} className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 hover:shadow-xl transition-all">
                <div className="w-14 h-14 bg-orange-600 rounded-2xl flex items-center justify-center text-white mb-6">
                  <SafeIcon icon={item.icon} className="text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Manage Your Entire Alberta Business From Your Pocket</h3>
              <p className="text-slate-600 mb-8">
                Whether you're in a basement in Calgary or on a roof in Edmonton, you need to be able to close deals instantly. Our mobile interface gives you the power of a full office staff right on your phone.
              </p>
              <ul className="space-y-4">
                {[
                  "Send invoices and collect payments via text",
                  "Automated follow-ups for cold leads",
                  "Real-time lead notifications",
                  "Call recording and tracking"
                ].map((text, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700 font-medium">
                    <div className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center shrink-0">
                      <SafeIcon icon="FiCheck" className="text-xs" />
                    </div>
                    {text}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-orange-600 rounded-3xl transform rotate-2"></div>
              <div className="relative bg-white p-8 rounded-3xl shadow-2xl border border-slate-100">
                <AuditForm 
                  type="CRM" 
                  title="See the Profit Command Center" 
                  subtitle="Get a personalized tour of how our CRM will streamline your specific workflow and increase your profit margins."
                  buttonText="Get My CRM Demo"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CRMService;